import {formatHex, hsvToHex} from '@teemill/utilities';
import {SierraCanvas, Vector, Animation} from '@teemill/common/classes';

import cloneDeep from 'lodash/cloneDeep';
import merge from 'deepmerge';

import {clamp} from 'lodash';

import {OmniControl} from './';

import {Mathf} from '../omnicanvas.utilities';

import lockIconSrc from './../resources/lock_64.png';

import {defaultControlLayer, perspectiveControlLayer} from './controlLayers';

const lockIcon = new Image();
lockIcon.src = lockIconSrc;

export default class OmniObject {
  className = 'Object';
  /**
   * @param controller
   * This is the OmniCanvas controller that this element belongs to
   *
   * @type OmniCanvas
   */
  controller = null;

  /**
   * @param enabled
   * If false, this element will not be rendered
   */
  enabled = true;

  /**
   * @param width
   * The width of the element in the canvas (in pixels)
   */
  width = 80;

  /**
   * @param height
   * The height of the element in the canvas (in pixels)
   */
  height = 80;

  /**
   * @param {Vector} position
   * The vector coordinates of the top left corner of this element
   * The coordinates will be relative to the top left corner of the bounds
   */
  position = new Vector(0, 0);

  /**
   * @param {Int} zoom
   * The z position of the object
   * This determines the order in which objects are rendered, with higher numbers appearing on top
   * This also affects the order of objects returned by Controller.run('getObjects')
   * Effectively, this is the sort order of canvas elements
   */
  z = 0;

  /**
   * @param {Multi} color
   * The colour of the element. Accepts all CSS legal color values
   * (hex, rgb, rgba etc.)
   */
  color = 'red';

  /**
   * @param {Float} opacity
   * A value between 0 (transparent) and 1 (opaque)
   */
  opacity = 1;

  /**
   * @param {Float} angle
   * The angle of rotation of this object, in degrees
   * Angle can be converted to radians via 'this.radianAngle(this.angle)'
   */
  angle = 0;

  /**
   * @param {Float} scale
   * The scale of this object
   */
  scale = 1;

  /**
   * @param {String} tag
   * Objects can be given a tag to identify them
   */
  tag = 'Object';

  /**
   * @param {String} source
   * Objects can be given a source to identify where they came from
   */
  source = 'na';

  /**
   * @param {String} composite
   * Determines the composite operation used when rendering this element
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
   */
  composite = 'source-over';

  /**
   * @param {Object} controls
   * A list of OmniControl objects for this element
   */
  controls = {};

  /**
   * @param {Bool} controllable
   * If false, any user actions will be ignored for this element
   */
  controllable = true;

  /**
   * @param {string} controlLayer
   * This defines which layer of controls is active on the element
   */
  controlLayer = defaultControlLayer;

  /**
   * @param {Image} cutout
   * If provided, this image will be used as a stencil cutout of the object
   */
  cutout = false;

  /**
   * @param {Object} shadow
   * Can be used to add a drop shadow effect to an object
   *
   * @property {Color} color Accepts any valid CSS color value
   * @property {Int} blur The pixel radius of the shadow blur
   * @property {Vector} offset The pixel offset of the shadow
   */
  shadow = {
    color: '',
    blur: 5,
    offset: new Vector(0, 0),
  };

  /**
   * @param customCursor
   * Define which mouse cursor type the user will see when hovering over this element
   * @see https://www.w3schools.com/cssref/pr_class_cursor.asp
   */
  customCursor = 'all-scroll';

  /**
   * @param {Gradient} gradient
   * Some objects accept gradient colour values
   * This will override the color property
   * @see omnicanvas.utilities.js/Gradient
   */
  gradient = null;

  /**
   * @param {Bool} interpolateGradient
   * If true, the gradient will be smoothed
   * If false, the gradient will be stepped
   */
  interpolateGradient = false;

  /**
   * @param {String} gradientDirection
   * one of 'X' (horizontal) or 'Y' (vertical)
   */
  gradientDirection = 'X';

  /**
   * @param {Object} postableParams
   * these will be appended to the returned value in the getPostableParams function
   */
  postableParams = false;

  /**
   * @param {Boolean} locked
   * If true, attempts to move or alter this object will be blocked
   */
  locked = false;

  /**
   * @type {any}
   */
  metadata = {};

  overlappingGroupId = null;

  /**
   * @param {string} applicationGroupRef
   * A reference string to link this element to an application group
   */
  applicationGroupRef = null;

  constructor(opts) {
    if (opts) {
      Object.keys(opts).forEach(i => {
        try {
          this[i] = opts[i];
          // eslint-disable-next-line
        } catch (e) {}
      });
    }
  }

  get canvasScale() {
    return this.controller?.params?.scale ?? 1;
  }

  get options() {
    if (!this.controller) {
      return {};
    }

    const optionName = this.className.toLowerCase();

    if (optionName !== 'object') {
      if (typeof this.controller?.params?.[optionName] === 'object') {
        return merge(
          this.controller?.params?.object,
          this.controller?.params?.[optionName]
        );
      }
    }

    return this.controller?.params?.object ?? {};
  }

  /**
   * @func init Initialises the element
   *
   * @param {OmniCanvas} controller The parent controller of this element
   * @return {void}
   */
  init(controller) {
    this.beforeInit();
    this.initControls();
    this.position = new Vector(this.position.x, this.position.y);

    if (this.perspective) {
      this.perspective = new Vector(this.perspective.x, this.perspective.y);
    }

    this.controller = controller;
    this.ctx = controller.renderer?.virtualCanvas;
    this.cnv = this.ctx.canvas;
    this.id = this.id ? this.id : Date.now();

    this.uniformScaling = this.uniformScaling
      ? this.uniformScaling
      : this.options.uniformScaling;

    if (!this.layer) {
      this.layer = controller.activeLayerName;
    }
    if (!this.z) {
      const objs = controller.getObjects();
      if (objs && objs.length) {
        const lastObject = objs[objs.length - 1];
        this.z = lastObject.z + 1;
      }
    }
    this.afterInit();

    if (this.options.onAfterInitialise) {
      this.options.onAfterInitialise(this);
    }
  }

  /**
   * @func initControls Creates the OmniControl objects for this element
   *
   * @return {void}
   */
  initControls() {
    this.controls = {
      rotScale: new OmniControl({
        placement: new Vector(1, 1),
        element: this,
        action(from, to) {
          this.element._rotateOrScale(from, to);
        },
        mouseDown(e) {
          this.aspectRatio = new Vector(
            this.element.width,
            this.element.height
          ).normalize();
          this.startPosition = this.element.position.scale(1);
          this.startAngle = this.element.angle;
          this.startWidth = this.element.width;
          this.startHeight =
            typeof this.element.fontSize === 'number' &&
            this.element.fontSize &&
            !isNaN(this.element.fontSize)
              ? this.element.fontSize
              : this.element.height;
          this.angleToControl =
            Math.atan(-this.position.y / this.position.x) * (180 / Math.PI);
          this.startVector = e;
          this.delta = new Vector(0, 0);
          this.mode = false;
        },
        mouseUp() {
          this.startPosition = false;
        },
      }),
    };
  }

  /**
   * @func beforeInit
   * This runs at the start of the init function
   * This is an abstract function that can be overwritten by child classes
   */
  beforeInit() {
    // nooop
  }

  /**
   * @func afterInit
   * This runs at the end of the init function
   * This is an abstract function that can be overwritten by child classes
   */
  afterInit() {
    // nooop
  }
  applyCutout() {
    // nooop
  }

  /**
   * @func beforeRender
   * This runs at the start of the render function
   * This is an abstract function that can be overwritten by child classes
   */
  beforeRender() {
    // nooop
  }

  /**
   * @func afterRender
   * This runs at the end of the render function
   * This is an abstract function that can be overwritten by child classes
   */
  afterRender() {
    // nooop
  }

  /**
   * @func beforeRenderBounds
   * This runs at the start of the renderBounds function
   * This is an abstract function that can be overwritten by child classes
   */
  beforeRenderBounds() {
    // nooop
  }

  /**
   * @func afterRenderBounds
   * This runs at the end of the renderBounds function
   * This is an abstract function that can be overwritten by child classes
   */
  afterRenderBounds() {
    // nooop
  }

  /**
   * @func formatColor Converts the color property into a hex value
   *
   * @return {void}
   */
  formatColor() {
    if (typeof this.color === 'number') {
      this.color = this.color.toString();
    }
    if (this.color && typeof this.color.match === 'function') {
      if (this.color.match(/#?(?:[0-9a-f]{3}){1,2}/)) {
        this.color = formatHex(this.color, true);
      }
    }
  }

  /**
   * @func preRender
   * This runs after beforeRender but before draw
   * beforeRender can be overwritten by child classes, but preRender should be left as is
   * @return {void}
   */
  preRender() {
    this.outerBounds = this.calculateOuterBounds();
    this.controller?.snapPosition?.(this);

    this.width *= this.scale;
    this.height *= this.scale;
    if (this.canvasScale !== 1) {
      this.width *= this.canvasScale;
      this.height *= this.canvasScale;

      // this.position.x *= this.canvasScale;
      // this.position.y *= this.canvasScale;
    }

    this.ctx.beginPath();

    this.ctx.globalAlpha = this.opacity;

    this.drawDebug();
    this.applyCanvasTransforms();

    this.ctx.globalAlpha = this.opacity;

    const centerPoint = this.centerPoint();

    this.ctx.translate(
      centerPoint.x + this.position.x * (this.canvasScale - 1),
      centerPoint.y + this.position.y * (this.canvasScale - 1)
    );

    this.applyRotation();

    this.ctx.translate(
      -(centerPoint.x + this.position.x * (this.canvasScale - 1)),
      -(centerPoint.y + this.position.y * (this.canvasScale - 1))
    );

    this.ctx.translate(
      this.position.x * this.canvasScale,
      this.position.y * this.canvasScale
    );
  }

  applyRotation() {
    this.ctx.rotate(this.radianAngle(-this.angle));
  }

  /**
   * @func calculateOuterBounds
   * Imagine a rectangle surrounding this object.
   * The object may be rotated but the rectangle's rotation is fixed.
   * This function calculates the coordinates of each corner of that rectangle
   * To see this in action, enable debugMode and look for the green dashed square
   * (controller.params.debugMode = true)
   *
   * @return {Vector[]}
   * The result of this function will be accessible via this.outerBounds
   */
  calculateOuterBounds() {
    const width = this.width * this.canvasScale;
    const height = this.height * this.canvasScale;
    const centerPoint = new Vector(width / 2, height / 2);
    const tl = new Vector(0, 0).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );
    const tr = new Vector(width, 0).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );
    const bl = new Vector(0, height).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );
    const br = new Vector(width, height).rotateAround(
      centerPoint.x,
      centerPoint.y,
      -this.angle
    );

    const minX = Math.min(tl.x, tr.x, bl.x, br.x);
    const maxX = Math.max(tl.x, tr.x, bl.x, br.x);
    const minY = Math.min(tl.y, tr.y, bl.y, br.y);
    const maxY = Math.max(tl.y, tr.y, bl.y, br.y);

    return {
      tl: new Vector(minX, minY),
      tr: new Vector(maxX, minY),
      bl: new Vector(minX, maxY),
      br: new Vector(maxX, maxY),
    };
  }

  /**
   * @func render Contains the entire render process for this element
   *
   * NOTE!: This function should not be modified
   * Instead, you can modify the following functions
   *  beforeRender - occurs at the very start of the render process
   *  draw - occurs in the middle, this is where the object is actually commited to canvas
   *  afterRender - occurs at the very end of the render process
   *
   * @param  {...any} args These arguments will be passed to the draw function
   * @return {void}
   */
  render(...args) {
    if (this.enabled && this.ctx) {
      this.formatColor();
      this.beforeRender();
      const savedproperties = {
        width: this.width,
        height: this.height,
        position: new Vector(this.position.x, this.position.y),
      };
      this.ctx.save();
      this.preRender();
      this.ctx.globalCompositeOperation = this.composite;
      this.draw(this, args);
      this.ctx.closePath();
      this.applyCutout();
      this.ctx.restore();

      this.afterRender();
      Object.assign(this, savedproperties);
    }
  }

  /**
   * @func draw Draws the object to the canvas
   *
   * @return {void}
   */
  draw() {
    this.ctx.save();
    this.drawShadow();

    if (this.gradient) {
      const gradient = this.gradient;
      this.ctx.angularGradientFill(
        gradient.toObject(),
        gradient.direction,
        0,
        0,
        Math.round(this.width),
        Math.round(this.height)
      );
    } else {
      this.ctx.fillStyle = this.color;

      this.ctx
        .beginPath()
        .rect(0, 0, Math.round(this.width), Math.round(this.height))
        .fill()
        .closePath();

      this.ctx.shadowColor = 'transparent';
    }

    this.ctx.restore();
  }
  /**
   * @func drawDebug If debug is enabled then various properties will be visualised
   * in the canvas to help with debugging
   *
   * green dashed square = this.outerBounds
   * red dashed circle = a circle passing through each corner of the object
   * blue dashed lines = lines crossing the centre of the object horizontally and vertically
   *  the intersection of these lines is the exact centre of the object
   * black arc = rotation of the object
   *
   * @return {void}
   */
  drawDebug() {
    if (!this.isGrouped && this.controller?.params?.debugMode) {
      const hypotenuse = Math.sqrt(
        (this.width / 2) ** 2 + (this.height / 2) ** 2
      );
      const centerPoint = this.centerPoint();

      this.ctx.save();
      this.ctx.lineWidth = 3;
      if (this.controller?.boundingBox) {
        this.ctx.translate(
          this.controller?.boundingBox?.position?.x ?? 0,
          this.controller?.boundingBox?.position?.y ?? 0
        );

        this.ctx.translate(
          this.position.x * (this.canvasScale - 1),
          this.position.y * (this.canvasScale - 1)
        );

        this.ctx.translate(centerPoint.x, centerPoint.y);
      }

      /* visualise a box that completely surrounds the object, with the edges of
       the box always being aligned to the edges of the bounding box */
      this.ctx.setLineDash([10]);
      this.ctx.strokeStyle = 'green';
      this.ctx.beginPath();
      this.ctx.strokeRect(
        -(this.width / 2) + this.outerBounds.tl.x,
        -(this.height / 2) + this.outerBounds.tl.y,
        this.outerBounds.br.x - this.outerBounds.tl.x,
        this.outerBounds.br.y - this.outerBounds.tl.y
      );
      this.ctx.closePath();

      this.ctx.beginPath();
      this.ctx.strokeStyle = 'red';
      this.ctx.arc(0, 0, hypotenuse, 0, Math.PI * 2);
      this.ctx.stroke();
      this.ctx.closePath();

      // visualises a line running through the object's vertical axis with length hypotenuse
      let linePoint = new Vector(0, hypotenuse).rotate(-this.angle);
      this.ctx.strokeStyle = 'blue';
      this.ctx.beginPath();
      this.ctx.moveTo(linePoint.x, linePoint.y);
      linePoint = linePoint.rotate(180);
      this.ctx.lineTo(linePoint.x, linePoint.y);
      this.ctx.stroke();
      this.ctx.closePath();

      this.ctx.fillStyle = 'yellow';

      this.ctx.fillRect(-4, -4, 8, 8);
      this.ctx.restore();
    }
  }

  /**
   * @func drawShadow Draw the shadow of an object, if a shadow has been
   * assigned to it. This shadow system is very similar to the standard CSS
   * box-shadow system.
   *
   * @return {void}
   */
  drawShadow() {
    if (this.shadow?.color && this.shadow?.offset) {
      this.ctx.shadowColor = this.shadow.color;
      this.ctx.shadowBlur = this.shadow.blur * this.canvasScale;
      this.ctx.shadowOffsetX = this.shadow.offset.x * this.canvasScale;
      this.ctx.shadowOffsetY = this.shadow.offset.y * this.canvasScale;
    }
  }

  /**
   * @func renderBounds Render the bounds of an object (the rect that extends
   * around an object)
   *
   * @return {void}
   */
  renderBounds() {
    if (!this.controller?.boundingBox?.options?.show) {
      return;
    }

    this.ctx.save().beginPath().rect(0, 0, this.width, this.height);

    let boundsHex;

    if (this.locked && this.options.lockedBoundsColor) {
      boundsHex = this.options.lockedBoundsColor;
    } else if (this.options.boundsColor) {
      boundsHex = this.options.boundsColor;
    } else {
      const rotatedLum =
        (1 + ((this.controller?.params?.baseLuminance ?? 0.5) - 0.5)) % 1;
      boundsHex = hsvToHex({h: 0, s: 0, v: rotatedLum});
    }

    this.ctx
      .setStrokeStyle(boundsHex)
      .setLineWidth(this.options.boundsLineWidth);

    if (this.options.boundsDashed) {
      this.ctx.setLineDash(this.options.boundsLineDash);
    }

    this.ctx.stroke().closePath().restore();

    if (this.options.showDimensions) {
      const cmDimensions = this.dimensionsInCm();
      const dimensionsString = cmDimensions
        ? `~${cmDimensions.width} x ${cmDimensions.height}${
            this.controller?.params?.object?.units ?? ''
          }`
        : '';

      this.ctx.save();
      this.ctx.font =
        this.controller?.isMobile?.() ?? false
          ? '32px Lato, sans-serif'
          : '18px Lato, sans-serif';
      const textWidth = this.ctx.measureText(dimensionsString).width;

      this.ctx
        .save()
        .beginPath()
        .setFillStyle('white')
        .setGlobalAlpha(0.75)
        .fillRect(
          this.width / 2 - textWidth / 2 - 5,
          -(this.controller?.isMobile?.() ?? false ? 44 : 28),
          textWidth + 10,
          this.controller?.isMobile?.() ?? false ? 42 : 26
        )
        .restore()
        .fillText(dimensionsString, this.width / 2 - textWidth / 2, -8)
        .restore();
    }

    if (this.locked) {
      const lockIconSize = this.options.lockIconSize;
      this.ctx
        .save()
        .setShadow(0, 0, 2, 'rgba(0,0,0,0.75)')
        .drawImage(
          lockIcon,
          this.width - lockIconSize / 2,
          -(lockIconSize / 2),
          lockIconSize,
          lockIconSize
        )
        .restore();
    }
  }

  /**
   * @func customBounds This is an abstract function that can be overwritten to
   * draw a custom bounds area rather than the default one. If this function
   * returns false then the default bounds will be rendered, else if it returns
   * true then the default bounds will not be rendered
   *
   * @return {Boolean}
   */
  customBounds() {
    return false;
  }

  /**
   * @func drawControls Draw the control elements for this element. The control
   * elements are OmniControl objects and typically appear at the
   * corners of the element
   *
   * @return {void}
   */
  drawControls() {
    const pixelScale = this.controller?.getPixelScale?.() ?? 1;
    Object.keys(this.controls)
      .filter(control => this.controls[control].layer === this.controlLayer)
      .forEach(i => {
        const control = this.controls[i];

        if (typeof control.icon === 'string') {
          const src = control.icon;
          control.icon = new Image();
          control.icon.crossOrigin = 'Anonymous';
          control.icon.src = src;
        }

        control.getPosition(this);
        let scale =
          this.controller?.isMobile?.() ?? false ? control.mobileScale : 1;
        scale *= pixelScale;

        const controlPos = control.position;

        if (control.render) {
          const canvasScale = this.canvasScale;

          this.ctx
            .save()
            .translate(controlPos.x, controlPos.y)
            .when(control.offset?.x || control.offset?.y, ctx =>
              ctx.translate(
                control.offset.x * (canvasScale - 1),
                control.offset.y * (canvasScale - 1)
              )
            )
            .scale(scale, scale);

          control.render(this.ctx);

          this.ctx.restore();
        } else if (control.icon) {
          this.ctx.drawImage(
            control.icon,
            Math.round(controlPos.x - (control.width / 2) * scale),
            Math.round(controlPos.y - (control.height / 2) * scale),
            Math.round(control.width * scale),
            Math.round(control.height * scale)
          );
        } else {
          this.ctx
            .save()
            .beginPath()
            .arc(controlPos.x, controlPos.y, 8 * scale, 0, Math.PI * 2)
            .setFillStyle('white')
            .fill()
            .setStrokeStyle('black')
            .stroke()
            .restore();
        }

        this.ctx.closePath();
      });
  }

  /**
   * @func centerPoint Calculate the pixel coordinates of the center of the
   * object in the stage
   *
   * @return {Vector}
   */
  centerPoint() {
    if (this.parent) {
      return new Vector(
        (this.position.x / this.canvasScale + this.width / 2) * this.scale,
        (this.position.y / this.canvasScale + this.height / 2) * this.scale
      );
    }
    return new Vector(
      (this.position.x + this.width / 2) * this.scale,
      (this.position.y + this.height / 2) * this.scale
    );
  }

  /**
   * @func radianAngle Converts an angle from degrees to radians
   * @param angle An angle in degrees
   * @return {Number} An angle in radians
   */
  radianAngle(angle) {
    return angle * 0.0174533;
  }

  /**
   * @func _rotateOrScale
   * @param {Vector} from
   * @param {Vector} to
   *
   * This is a combined handler for rotation and scale
   * One control can be used for both functions, and it is the way that the user
   * uses the control that determines which to use
   * This function will call either rotScaleScale or rotScaleRotate
   * The decision of which one to use is based on whether the user drags
   * away or towards the top left corner of the object (scale)
   * or to the side and around the object (rotate)
   * @return {void}
   */
  _rotateOrScale(from, to) {
    if (this.locked) {
      return;
    }

    from = from.rotateAround(to.x, to.y, this.angle);
    const rotScale = this.controls.rotScale;
    const centerPoint = this.centerPoint();

    const angleToStart = to.angleTo(centerPoint) * (1 / to.degree2rad);
    const delta = rotScale.delta.translate(to.x - from.x, to.y - from.y);

    const angle =
      (rotScale.angleToControl + rotScale.startAngle + angleToStart) % 360;
    const line1 = rotScale.aspectRatio.rotate(-rotScale.startAngle).normalize();
    const line2 = new Vector(
      to.x - rotScale.startVector.x,
      to.y - rotScale.startVector.y
    ).normalize();

    const angleToSlope1 = Math.PI / 2 - Math.atan(line1.y / line1.x);
    const angleToSlope2 = Math.PI / 2 - Math.atan(line2.y / line2.x);

    const scaleFactor = 1 - Math.abs(Math.sin(angleToSlope2 - angleToSlope1));

    const direction =
      to.deltaTo(centerPoint).magnitude() >
      rotScale.startVector.deltaTo(centerPoint).magnitude()
        ? 1
        : -1;
    let distance =
      to.distanceTo(rotScale.startVector) * (scaleFactor * direction);

    if (isNaN(distance)) {
      distance = 0;
    }
    if (!rotScale.mode) {
      rotScale.mode = 'scale';
    }

    if (rotScale.mode === 'rotate') {
      if (Math.abs(angle) < 5 && Math.abs(distance) > 5) {
        rotScale.mode = 'scale';
      }
    } else if (rotScale.mode === 'scale') {
      if (Math.abs(distance) < 5 && Math.abs(angle) > 5) {
        rotScale.mode = 'rotate';
      }
    }

    rotScale.delta = delta;
    if (rotScale.mode === 'rotate') {
      this.rotScaleRotate(-(rotScale.angleToControl + angleToStart));

      this.afterRotate();
    } else if (rotScale.mode === 'scale') {
      this.rotScaleScale(distance);

      this.afterScale();
    }

    this.afterRotateOrScale();

    this.controller?.render?.();
  }

  /**
   * The rotScale handler for rotation. Applies rotation to the object
   *
   * @param {Number} newAngle The new angle to rotate to (degrees)
   * @return {void}
   */
  rotScaleRotate(newAngle) {
    const rotScale = this.controls.rotScale;
    this.angle = this.controller?.snapAngle?.(newAngle);
    this.width = rotScale.startWidth;
    this.height = rotScale.startHeight;
  }

  /**
   * rotScale handler for scale. Applies the new scale to the object
   *
   * @param {Number} distance The distance of scale. Imagine a line going
   * from the center of a rectangle to the bottom right corner. Then imagine
   * stretching that line further beyond the bottom right corner to a new point.
   * The distance between the bottom right corner and the new point is this
   * distance.
   * @return {void}
   */
  rotScaleScale(distance) {
    distance *= this.canvasScale;
    const rotScale = this.controls.rotScale;
    this.angle = rotScale.startAngle;
    this.width =
      rotScale.startWidth +
      distance * rotScale.aspectRatio.x * (1 / this.canvasScale);

    this.height =
      rotScale.startHeight +
      distance * rotScale.aspectRatio.y * (1 / this.canvasScale);

    /* imagine a circle running around the object and passing through each of the 4 corners.
      This finds the point of the top left corner of the object on that circle, accounting for
      the object's rotation. The object then has to be translated by that vector so that the
      object is scaled from the top left corner */
    const translationVector = new Vector(0, 0)
      .rotateAround(
        rotScale.aspectRatio.normalize().x / 2,
        rotScale.aspectRatio.normalize().y / 2,
        -this.angle
      )
      .scale(distance);

    this.position.x =
      rotScale.startPosition.x - translationVector.x / this.canvasScale;
    this.position.y =
      rotScale.startPosition.y - translationVector.y / this.canvasScale;

    if (this.width < 0) {
      this.angle += 90;
      this.width *= -1;
      this.position.x -= this.width;
    }
    if (this.height < 0) {
      this.angle += 90;
      this.height *= -1;
      this.position.y -= this.height;
    }
  }

  /**
   * Apply scale and translates to the canvas 2d rendering context to match the
   * scale, position and translation of the canvas and object
   *
   * @return {void}
   */
  applyCanvasTransforms() {
    if (this.controller.boundingBox) {
      this.ctx.translate(
        this.controller.boundingBox.position.x,
        this.controller.boundingBox.position.y
      );
    }

    this.ctx.translate(
      this.controller?.params?.globalTranslation?.x ?? 0,
      this.controller?.params?.globalTranslation?.y ?? 0
    );
  }

  /**
   * @func angleBetween Returns the angle in degrees between two points
   *
   * @param {Vector} p1 A coordinate in space
   * @param {Vector} p2 Another coordinate in space
   *
   * @return {Float} An angle in degrees between p1 and p2
   * The angle will have a value between -180 and 180
   * 0 means p1 is directly below/south of p2
   * 90 means p1 is directly right/east of p2
   * 180 or -180 means p1 is directly above/north of p2
   * -90 means p1 is directly left/west of p2
   */
  angleBetween(p1, p2) {
    return Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
  }

  /**
   * Update the colour of the element
   *
   * @param {String} color The colour of the element
   * @return {void}
   */
  setColor(color) {
    this.color = color;
    this.controller?.render?.();
  }

  /**
   * This is an abstract function that can be used to override the default
   * move behaviour. If this returns false, the default behaviour will take
   * place. If this returns true, the default behaviour will not take place
   *
   * @return {Boolean}
   */
  customMoveFunction() {
    return false;
  }

  /**
   * These handlers can be used to apply custom logic after rotating or scaling
   * the element
   */
  afterRotate() {}
  afterScale() {}
  afterRotateOrScale() {}

  /**
   * This is an abstract function that happens when the mouseUp event is
   * triggered on this object.
   *
   * @return {void}
   */
  mouseUp() {
    // noop
  }

  /**
   * This is an abstract function that happens when the mouseDown event is
   * triggered on this object.
   *
   * @return {void}
   */
  mouseDown() {
    // noop
  }

  /**
   * Moves this object up one layer, and moves the object that currently
   * occupies that layer down into this object's current layer
   *
   * @return {void}
   */
  moveLayerUp() {
    const objects = this.controller?.getObjects?.() ?? [];
    const l = objects.length;
    let closestObject = false;
    let closestZ = Infinity;
    const startZ = this.z;
    for (let i = 0; i < l; ++i) {
      if (objects[i].z > startZ && objects[i].z < closestZ) {
        closestObject = objects[i];
        closestZ = closestObject.z;
      }
    }
    if (closestObject) {
      this.z = closestZ;
      closestObject.z = startZ;
    } else if (this.z < this.controller?.getObjects?.() ?? [].length) {
      this.z += 1;
    }
    this.controller?.render?.();
  }

  /**
   * Moves this object down one layer, and moves the object that currently
   * occupies that layer up into this object's current layer
   *
   * @return {void}
   */
  moveLayerDown() {
    const objects = this.controller?.getObjects?.() ?? [];
    const l = objects.length;
    let closestObject = false;
    let closestZ = -Infinity;
    const startZ = this.z;
    for (let i = 0; i < l; ++i) {
      if (objects[i].z < startZ && objects[i].z > closestZ) {
        closestObject = objects[i];
        closestZ = closestObject.z;
      }
    }
    if (closestObject) {
      this.z = closestZ;
      closestObject.z = startZ;
    } else if (this.z > 0) {
      this.z -= 1;
    }
    this.controller?.render?.();
  }

  /**
   * Moves this object down to the very bottom layer, and any objects between
   * this and the bottom will move up by one layer
   *
   * @return {void}
   */
  moveToBack() {
    this.switchLayer(0);
  }

  /**
   * Moves this object up to the very top layer, and any objects between this
   * and the top will move down by one layer
   *
   * @return {void}
   */
  moveToFront() {
    this.switchLayer(Infinity);
  }

  /**
   * Switch to a specified layer. Any objects between this and the target layer
   * will also move accordingly.
   *
   * @param {Number} to The number of the layer, where 0 is the bottom layer
   * and each layer above that is 1 higher than the last
   * @return {void}
   */
  switchLayer(to) {
    to = Mathf.clamp(to, 0, this.controller?.params?.objects?.length ?? 0 - 1);
    const objects = this.controller?.getObjects?.() ?? [];
    const l = objects.length;
    const dir = this.z > to ? 1 : -1;
    const min = Math.min(this.z, to);
    const max = Math.max(this.z, to);

    for (let i = 0; i < l; ++i) {
      if (objects[i].z >= min && objects[i].z <= max) {
        objects[i].z += dir;
      }
    }
    this.z = to;
    this.controller?.render?.();
  }

  /**
   * Resizes the object by a specified amount
   *
   * @param {Number} scaleFactor the rate to scale by, where 1 is no scaling at all
   */
  resize(scaleFactor) {
    this.translate(
      (this.width - this.width * scaleFactor) / 2,
      (this.height - this.height * scaleFactor) / 2
    );

    this.width *= scaleFactor;
    this.height *= scaleFactor;

    this.controller?.render?.();
  }

  /**
   * Reduces or increases opacity by a specified amount
   *
   * @param {Number} modification how much to alter the opacity by
   */
  changeOpacity(modification) {
    this.opacity += modification;

    this.opacity = clamp(this.opacity, 0, 1);

    this.controller?.render?.();
  }

  /**
   * @func isOut Detects if the object is outside of the design area
   *
   * @return {Bool}
   * Returns true if even 1 pixel of the object is outside
   */
  isOut() {
    // if there are no outer bounds then the design is never out of bounds
    if (!this.outerBounds) {
      return false;
    }

    if (
      this.position.x + this.outerBounds.tl.x < 0 ||
      this.position.x + this.outerBounds.br.x >
        (this.controller?.boundingBox?.width ?? 0) ||
      this.position.y + this.outerBounds.tl.y < 0 ||
      this.position.y + this.outerBounds.br.y >
        (this.controller?.boundingBox?.height ?? 0)
    ) {
      return true;
    }
    return false;
  }

  /**
   * @func isIn Detects if the object is inside the design area
   *
   * @return {Bool}
   * Returns false if even 1 pixel of the object is inside
   * Note that this is not always equal to isOut, because if a design crosses the
   * bounds it is both in and out
   */
  isIn() {
    // if there are no outer bounds then the design is always in bounds
    if (!this.outerBounds) {
      return true;
    }

    if (
      this.position.x + this.outerBounds.br.x > 0 &&
      this.position.x + this.outerBounds.tl.x <
        (this.controller?.boundingBox?.width ?? 0) &&
      this.position.y + this.outerBounds.br.y > 0 &&
      this.position.y + this.outerBounds.tl.y <
        (this.controller?.boundingBox?.height ?? 0)
    ) {
      return true;
    }
    return false;
  }

  overlaps(otherObject) {
    const a = this.outerBounds ?? this.calculateOuterBounds();
    const b = otherObject.outerBounds ?? otherObject.calculateOuterBounds();

    const aLeft = this.position.x + a.tl.x;
    const aRight = this.position.x + a.br.x;
    const aTop = this.position.y + a.tl.y;
    const aBottom = this.position.y + a.br.y;

    const bLeft = otherObject.position.x + b.tl.x;
    const bRight = otherObject.position.x + b.br.x;
    const bTop = otherObject.position.y + b.tl.y;
    const bBottom = otherObject.position.y + b.br.y;

    if (aLeft >= bRight || bLeft >= aRight) {
      return false;
    }

    if (aTop >= bBottom || bTop >= aBottom) {
      return false;
    }

    return true;
  }

  /**
   * This is an abstract function for when a user keys down while this object is
   * active.
   *
   * @return {void}
   */
  onKeyDown(e) {
    // only run if the event occurs on something that doesn't belong to a form
    if (
      e.target.nodeName !== 'INPUT' &&
      e.target.nodeName !== 'TEXTAREA' &&
      !e.target.closest('form')
    ) {
      // not all keydown events have a code
      if (e.code) {
        if (e.ctrlKey) {
          if (e.code === 'KeyL') {
            e.preventDefault();
            this.locked = !this.locked;
            this.controller?.render?.();
          }
        }

        if (this.locked) {
          return;
        }
        // arrow controls
        // shift the object on arrow keys pressed
        if (e.code.substring(0, 5) === 'Arrow') {
          if (e.code === 'ArrowUp') {
            e.preventDefault();
            this.translate(0, -1);
          } else if (e.code === 'ArrowDown') {
            e.preventDefault();
            this.translate(0, 1);
          } else if (e.code === 'ArrowLeft') {
            this.translate(-1, 0);
          } else if (e.code === 'ArrowRight') {
            this.translate(1, 0);
          }

          this.controller?.render?.();
        }

        if (e.code === 'PageDown') {
          e.preventDefault();
          this.moveLayerDown();
        } else if (e.code === 'PageUp') {
          e.preventDefault();
          this.moveLayerUp();
        }

        const scaleFactor = 1.025;

        if (e.code === 'Minus') {
          this.resize(1 / scaleFactor);
        } else if (e.code === 'Equal') {
          this.resize(scaleFactor);
        }

        if (e.code === 'Comma') {
          this.changeOpacity(-0.05);
        } else if (e.code === 'Period') {
          this.changeOpacity(0.05);
        }

        // rotation controls
        // rotate the object on [ ] brackets pressed
        if (e.code.substring(0, 7) === 'Bracket') {
          if (e.code === 'BracketLeft') {
            this.angle += 1;
          } else if (e.code === 'BracketRight') {
            this.angle -= 1;
          }

          this.controller?.render?.();
        }
      }
    }
  }

  /**
   * This is an abstract function for when a user keys up while this object is
   * active.
   *
   * @return {Boolean}
   */
  onKeyUp() {
    return false;
  }

  /**
   * @func getPostableParams
   * Returns various properties of this object that can be posted to a server
   *
   * The returned object can be extended via this.extendPostableParams
   * @return {Object}
   */
  getPostableParams(includeDimensions) {
    if (!this.enabled) {
      return {};
    }
    const post = {
      angle: this.angle,
      color: this.color,
      class: this.class,
      composite: this.composite,
      scale: this.scale,
      zoom: 1,
      position: {
        x: this.position.x + this.controller?.params?.globalTranslation?.x ?? 0,
        y: this.position.y + this.controller?.params?.globalTranslation?.y ?? 0,
      },
      shadow: this.shadow
        ? {
            blur: this.shadow?.blur,
            color: this.shadow?.color,
            offset: this.shadow?.offset
              ? {
                  x: this.shadow?.offset?.x,
                  y: this.shadow?.offset?.y,
                }
              : {
                  x: 0,
                  y: 0,
                },
          }
        : {},
      z: this.z,
      width: this.width,
      height: this.height,
      opacity: this.opacity,
      options: this.postableParams,
      id: this.id,
      marginBottom: this.marginBottom,
    };
    if (includeDimensions) {
      post.height = this.height;
      post.width = this.width;
    }
    this.extendPostableParams(post);
    return post;
  }

  extendPostableParams() {
    // noop
  }

  /**
   * This is an abstract function that is triggered when an element is clicked
   *
   * @return {void}
   */
  click() {
    // noop
  }

  /**
   * This is an abstract function that is triggered when an element is selected
   *
   * @return {void}
   */
  select(event) {
    this.controller?.onElementSelected?.(this);
    this.onSelect(event);
  }

  onSelect(event) {}

  /**
   * This is an abstract function that is triggered when an element is deselected
   *
   * @return {void}
   */
  deselect(event) {
    this.controller?.onElementDeselected?.(this);
    this.onDeselect(event);
  }

  onDeselect(event) {}

  /**
   * Lerp. Linear interpolation.
   * Animate between two values with no smoothing
   *
   * @param {string} param The param to animate. Should be provided in dot notation
   * @param {number} from The starting value
   * @param {number} to The end value
   * @param {number} duration The duration of the animation, in milliseconds
   */
  lerp(param, from, to, duration) {
    this.animate(param, from, to, duration, 'lerp');
  }

  /**
   * Berp. Bezier interpolation.
   * Animate between two values with bezier smoothing. Ease-in-out
   *
   * @param {string} param The param to animate. Should be provided in dot notation
   * @param {number} from The starting value
   * @param {number} to The end value
   * @param {number} duration The duration of the animation, in milliseconds
   */
  berp(param, from, to, duration) {
    this.animate(param, from, to, duration, 'berp');
  }

  /**
   * Qerp. Quadratic interpolation.
   * Animate between two values with quadratic smoothing. Starts fast, ends slow.
   *
   * @param {string} param The param to animate. Should be provided in dot notation
   * @param {number} from The starting value
   * @param {number} to The end value
   * @param {number} duration The duration of the animation, in milliseconds
   */

  qerp(param, from, to, duration) {
    this.animate(param, from, to, duration, 'qerp');
  }

  activeAnimations = {};

  /**
   * Animates a variable between two variables over a specified length of time
   * ! Note: This method shouldn't be called directly. Instead call one of
   * ! lerp, berp or qerp
   *
   * @param {string} param The param to animate. Should be provided in dot notation
   * @param {number} from The starting value
   * @param {number} to The end value
   * @param {number} duration The duration of the animation, in milliseconds
   * @param {string} method The animation method to use. One of lerp, berp, qerp
   */
  animate(param, from, to, duration, method) {
    if (!this.controller.params.stage.animationsEnabled) {
      this.applyParamByDotNotation(param, to);
      return;
    }

    const animation = new Animation('number');
    animation.start(from, to, duration);

    this.activeAnimations[param] = {
      animation,
      from,
      to,
      method,
    };

    this.controller?.requestAnimation?.('applyAnimation');
  }

  /**
   * Updates the state of animated variables to the current frame.
   */
  applyAnimation() {
    const animatingVariables = Object.keys(this.activeAnimations);

    animatingVariables.forEach(param => {
      const animationData = this.activeAnimations[param];

      this.applyParamByDotNotation(
        param,
        animationData.animation[animationData.method]
      );

      if (animationData.animation.ended) {
        this.removeAnimation(param);
      }
    });

    if (Object.keys(this.activeAnimations).length) {
      this.controller?.requestAnimation?.('applyAnimation');
    }
  }

  /**
   * Immediately stops an animation and prevents it from updating anymore
   *
   * @param {string} param dot-notation parameter name
   */
  removeAnimation(param) {
    delete this.activeAnimations[param];
  }

  /**
   * @param {string} param dot-notation parameter name
   * @param {*} value The value of the parameter to update
   */
  applyParamByDotNotation(param, value) {
    const paramParts = param.split('.');
    const paramsCount = paramParts.length - 1;

    // eslint-disable-next-line
    let activeObject = this;

    for (let i = 0; i < paramsCount; ++i) {
      activeObject = activeObject[paramParts[i]];
    }

    activeObject[paramParts[paramsCount]] = value;
  }

  /**
   * Checks if this object is the current active and selected element
   *
   * @return {Boolean}
   */
  isActiveElement() {
    return this.controller.activeEl
      ? this.controller.activeEl.id === this.id
      : false;
  }

  /**
   * Returns an object that represents all the differences between this element
   * and the base element. i.e. the difference between an OmniImage
   * element and a brand new OmniImage element
   *
   * @return {Object}
   */
  toDelta() {
    const original = new this.constructor();

    // TODO: Refactor objects to automatically exclude private properties
    const deltaObj = this.controller.comparePropertiesInObjects(
      this,
      original,
      ['baseControls', 'perspectiveMode', 'perspectiveCanvas']
    );
    deltaObj.className = this.className;

    if (deltaObj.position) {
      deltaObj.position = Object.assign({x: 0, y: 0}, deltaObj.position);
    }

    const boundingSubset = this._getBoundingSubsetData();
    if (boundingSubset) {
      deltaObj.boundingSubset = boundingSubset;
    }

    return deltaObj;
  }

  _enforceCleanDeltaPosition(delta) {
    if (delta.position) {
      delta.position = Object.assign({x: 0, y: 0}, delta.position);
    }
  }

  /**
   * Calculates the approximate cm dimensions of this element
   *
   * @return {Object}
   */
  dimensionsInCm() {
    const bounds = this.controller.boundingBox;
    const scale = this.canvasScale;
    let ppcm = bounds.pixelsPerCm;
    const rightEdge = Mathf.clamp(
      (this.position.x + this.width) / scale,
      0,
      bounds.width
    );
    const bottomEdge = Mathf.clamp(
      (this.position.y + this.height) / scale,
      0,
      bounds.height
    );
    const posX = Mathf.clamp(this.position.x / scale, 0, bounds.width);
    const posY = Mathf.clamp(this.position.y / scale, 0, bounds.height);
    const width = rightEdge - posX;
    const height = bottomEdge - posY;
    if (!ppcm) {
      return false;
    }

    if (this.options.units === 'in') {
      ppcm *= 2.54;
    }
    return {
      width: width ? Math.round((width / ppcm) * 10) / 10 : 0,
      height: height ? Math.round((height / ppcm) * 10) / 10 : 0,
    };
  }

  /**
   * Export this element as an image, cropped to its visual bounds.
   *
   * @param {Object} options
   * @param {String} options.format One of image/png, image/jpeg, image/webp, imageData
   * @param {Number} options.quality Image quality for jpeg/webp (0-1)
   * @param {Number} options.scale Export scale multiplier
   * @param {Number} options.padding Padding applied on all sides
   * @param {Number} options.paddingX Horizontal padding override
   * @param {Number} options.paddingY Vertical padding override
   * @param {Boolean} options.asBlob If true, resolve a Blob instead of base64
   * @param {Boolean} options.antiAliasingEnabled Toggle anti-aliasing during export
   * @return {Promise<Blob>|String|ImageData}
   */
  exportElement({
    format = 'image/png',
    quality = 0.92,
    scale = 1,
    padding = 0,
    paddingX = null,
    paddingY = null,
    asBlob = false,
    antiAliasingEnabled = true,
  } = {}) {
    if (!this.controller?.renderer) {
      return null;
    }

    const renderer = this.controller.renderer;
    const objects = this.controller.getObjects(
      this.layer ?? this.controller.activeLayerName
    );
    const visibilitySnapshot = objects.map(obj => ({
      obj,
      enabled: obj.enabled,
    }));

    let outputCanvas;
    let cleanupDone = false;

    visibilitySnapshot.forEach(({obj}) => {
      obj.enabled = obj.id === this.id;
    });

    const stashedParams = renderer.stashExportParams();
    renderer.engineParams.exportMode = true;

    const cleanup = result => {
      if (cleanupDone) {
        return result;
      }

      cleanupDone = true;
      visibilitySnapshot.forEach(({obj, enabled}) => {
        obj.enabled = enabled;
      });
      if (stashedParams?.boundingBox) {
        renderer.boundingBox.position.x = stashedParams.boundingBox.x;
        renderer.boundingBox.position.y = stashedParams.boundingBox.y;
      }
      renderer.restoreExportParams();
      renderer.engineParams.exportMode = false;
      renderer.render();
      outputCanvas?.destroy?.();
      return result;
    };

    try {
      renderer.prepareExport(scale, 1, antiAliasingEnabled);

      this.outerBounds = this.calculateOuterBounds();

      const canvasScale = renderer.engineParams.scale ?? scale;
      const boundsLeft = this.position.x * canvasScale + this.outerBounds.tl.x;
      const boundsTop = this.position.y * canvasScale + this.outerBounds.tl.y;
      const boundsWidth = this.outerBounds.br.x - this.outerBounds.tl.x;
      const boundsHeight = this.outerBounds.br.y - this.outerBounds.tl.y;

      const padX = (paddingX ?? padding) * canvasScale;
      const padY = (paddingY ?? padding) * canvasScale;

      const cropLeft = Math.round(boundsLeft - padX);
      const cropTop = Math.round(boundsTop - padY);
      const cropWidth = Math.max(1, Math.round(boundsWidth + padX * 2));
      const cropHeight = Math.max(1, Math.round(boundsHeight + padY * 2));

      renderer.render({
        includeBounds: false,
        includeStaticLayerImages: false,
        withAntiAliasing: antiAliasingEnabled,
        async: false,
      });

      const sourceCanvas = renderer.virtualCanvas.canvas;
      const boundsData = this.getClippedBounds({
        scale: canvasScale,
        stageWidth: sourceCanvas.width,
        stageHeight: sourceCanvas.height,
      });
      const stageBounds = boundsData?.stageBounds;

      const stageLeft = stageBounds?.left ?? 0;
      const stageTop = stageBounds?.top ?? 0;
      const stageRight = stageBounds?.right ?? sourceCanvas.width;
      const stageBottom = stageBounds?.bottom ?? sourceCanvas.height;

      const croppedLeft = Math.max(cropLeft, stageLeft);
      const croppedTop = Math.max(cropTop, stageTop);
      const croppedRight = Math.min(cropLeft + cropWidth, stageRight);
      const croppedBottom = Math.min(cropTop + cropHeight, stageBottom);

      const integerLeft = Math.max(0, Math.floor(croppedLeft));
      const integerTop = Math.max(0, Math.floor(croppedTop));
      const integerRight = Math.max(
        integerLeft,
        Math.min(sourceCanvas.width, Math.ceil(croppedRight))
      );
      const integerBottom = Math.max(
        integerTop,
        Math.min(sourceCanvas.height, Math.ceil(croppedBottom))
      );

      const visibleWidth = Math.max(0, integerRight - integerLeft);
      const visibleHeight = Math.max(0, integerBottom - integerTop);
      const outputWidth = Math.max(1, visibleWidth);
      const outputHeight = Math.max(1, visibleHeight);

      outputCanvas = new SierraCanvas(outputWidth, outputHeight);

      if (visibleWidth > 0 && visibleHeight > 0) {
        outputCanvas.drawImage(
          sourceCanvas,
          integerLeft,
          integerTop,
          visibleWidth,
          visibleHeight,
          0,
          0,
          visibleWidth,
          visibleHeight
        );
      }

      if (format === 'imageData') {
        const data = outputCanvas.getImageData(0, 0, outputWidth, outputHeight);
        return cleanup(data);
      }

      if (asBlob) {
        return new Promise(resolve => {
          try {
            outputCanvas.toBlob(
              blob => resolve(cleanup(blob)),
              format,
              quality
            );
          } catch (error) {
            cleanup();
            throw error;
          }
        });
      }

      const dataUrl = outputCanvas.toDataURL(format, quality);
      return cleanup(dataUrl);
    } catch (error) {
      cleanup();
      throw error;
    }
  }

  getClippedBounds({
    scale = this.canvasScale ?? 1,
    stageWidth,
    stageHeight,
  } = {}) {
    const outerBounds = this.outerBounds ?? this.calculateOuterBounds();
    if (!outerBounds) {
      return null;
    }

    const objectBounds = {
      left: this.position.x * scale + outerBounds.tl.x,
      top: this.position.y * scale + outerBounds.tl.y,
      right: this.position.x * scale + outerBounds.br.x,
      bottom: this.position.y * scale + outerBounds.br.y,
      width: outerBounds.br.x - outerBounds.tl.x,
      height: outerBounds.br.y - outerBounds.tl.y,
    };

    const stageParams = this.controller?.params?.stage ?? {};
    const boundingBox = this.controller?.boundingBox;

    const stageSpanWidth =
      typeof boundingBox?.width === 'number'
        ? boundingBox.width * scale
        : typeof stageWidth === 'number'
        ? stageWidth
        : typeof stageParams.width === 'number'
        ? stageParams.width * scale
        : objectBounds.width;
    const stageSpanHeight =
      typeof boundingBox?.height === 'number'
        ? boundingBox.height * scale
        : typeof stageHeight === 'number'
        ? stageHeight
        : typeof stageParams.height === 'number'
        ? stageParams.height * scale
        : objectBounds.height;

    const stageLeft = 0;
    const stageTop = 0;

    const stageBounds = {
      left: stageLeft,
      top: stageTop,
      right: stageLeft + stageSpanWidth,
      bottom: stageTop + stageSpanHeight,
      width: stageSpanWidth,
      height: stageSpanHeight,
    };

    if (typeof stageWidth === 'number') {
      stageBounds.left = Math.max(stageBounds.left, 0);
      stageBounds.right = Math.min(stageBounds.right, stageWidth);
    }

    if (typeof stageHeight === 'number') {
      stageBounds.top = Math.max(stageBounds.top, 0);
      stageBounds.bottom = Math.min(stageBounds.bottom, stageHeight);
    }

    stageBounds.right = Math.max(stageBounds.right, stageBounds.left);
    stageBounds.bottom = Math.max(stageBounds.bottom, stageBounds.top);
    stageBounds.width = stageBounds.right - stageBounds.left;
    stageBounds.height = stageBounds.bottom - stageBounds.top;

    const clippedLeft = Math.max(objectBounds.left, stageBounds.left);
    const clippedTop = Math.max(objectBounds.top, stageBounds.top);
    const clippedRight = Math.min(objectBounds.right, stageBounds.right);
    const clippedBottom = Math.min(objectBounds.bottom, stageBounds.bottom);

    const clippedWidth = Math.max(0, clippedRight - clippedLeft);
    const clippedHeight = Math.max(0, clippedBottom - clippedTop);

    return {
      scale,
      objectBounds,
      stageBounds,
      clipped: {
        left: clippedLeft,
        top: clippedTop,
        right: clippedRight,
        bottom: clippedBottom,
        width: clippedWidth,
        height: clippedHeight,
      },
    };
  }

  _getBoundingSubsetData() {
    const boundsData = this.getClippedBounds();
    if (!boundsData) {
      return null;
    }

    const {scale, objectBounds, stageBounds, clipped} = boundsData;

    if (!Number.isFinite(scale) || scale === 0) {
      return null;
    }

    if (clipped.width <= 0 || clipped.height <= 0) {
      return null;
    }

    return {
      stage: {
        x: clipped.left,
        y: clipped.top,
        width: clipped.width,
        height: clipped.height,
      },
      object: {
        x: (clipped.left - objectBounds.left) / scale,
        y: (clipped.top - objectBounds.top) / scale,
        width: clipped.width / scale,
        height: clipped.height / scale,
      },
      bounds: {
        x: stageBounds.left,
        y: stageBounds.top,
        width: stageBounds.right - stageBounds.left,
        height: stageBounds.bottom - stageBounds.top,
      },
    };
  }

  distanceToFurthestBorder() {
    const objectBoundingBox = this.outerBounds;

    const position = this.position;

    const bounds = this.controller.boundingBox;

    const topLeftCorner = new Vector(0, 0);
    const bottomRightCorner = new Vector(bounds.width, bounds.height);

    const topLeftDelta = objectBoundingBox.tl
      .translate(position.x, position.y)
      .deltaTo(topLeftCorner);

    const bottomRightDelta = objectBoundingBox.br
      .translate(position.x, position.y)
      .deltaTo(bottomRightCorner);

    return new Vector(
      Math.max(Math.abs(topLeftDelta.x), Math.abs(bottomRightDelta.x)),
      Math.max(Math.abs(topLeftDelta.y), Math.abs(bottomRightDelta.y))
    );
  }

  distanceToFurthestCorner() {
    const objectBoundingBox = this.outerBounds;

    const position = this.position;

    const bounds = this.controller.boundingBox;

    const topLeftCorner = new Vector(0, 0);
    const topRightCorner = new Vector(0, bounds.height);
    const bottomRightCorner = new Vector(bounds.width, bounds.height);
    const bottomLeftCorner = new Vector(bounds.width, 0);

    return Math.max(
      objectBoundingBox.tl
        .translate(position.x, position.y)
        .distanceTo(topLeftCorner),
      objectBoundingBox.tr
        .translate(position.x, position.y)
        .distanceTo(topRightCorner),
      objectBoundingBox.br
        .translate(position.x, position.y)
        .distanceTo(bottomRightCorner),
      objectBoundingBox.bl
        .translate(position.x, position.y)
        .distanceTo(bottomLeftCorner)
    );
  }

  onBeforeDelete() {
    if (this.perspectiveCanvas) {
      this.perspectiveCanvas.destroy();
    }

    this.ctx = null;
    this.cnv = null;
  }

  /**
   * Removes this element from the canvas
   *
   * @param {Event} e The OmniCanvasEvent
   *
   * @return {void}
   */
  delete(e) {
    if (!this.selectedErasable(e)) {
      this.controller.deleteObject(this);
    }
  }

  /**
   * Checks if current selected element is erasable.
   * For example, Text input.
   *
   * @protected
   *
   * @param {Event} e The OmniCanvasEvent
   *
   * @return {boolean} Is erasable element
   */
  selectedErasable(e) {
    const target = e?.originalTarget || e?.target;

    return target
      ? target.nodeName === 'TEXTAREA' || target.nodeName === 'INPUT'
      : false;
  }

  /**
   * Creates a duplicate of this element and places it in the canvas with a
   * slight offset.
   *
   * @return {OmniObject}
   */
  duplicate() {
    let newObjectOptions = this.toDelta();

    newObjectOptions.position = this.position.translate(10, 10);

    newObjectOptions = this.controller.cleanTemplate(newObjectOptions);

    newObjectOptions.id = null;

    const newObject = this.controller.insert(
      new this.constructor(cloneDeep(newObjectOptions))
    );
    this.controller.activeEl = newObject;
    this.controller?.render?.();

    return newObject;
  }

  togglePerspectiveMode() {
    if (!this.perspective) {
      this.perspective = new Vector(1, 0.5);
    }

    if (this.controlLayer !== perspectiveControlLayer) {
      this.enablePerspectiveMode();
    } else {
      this.disablePerspectiveMode();
    }

    this.controller?.render?.();
  }

  enablePerspectiveMode() {
    if (!this.controls.perspective) {
      this.controls.perspective = new OmniControl({
        layer: perspectiveControlLayer,
        placement: new Vector(0.5, 0.5),
        element: this,
        action(from, to) {
          this.element._shiftPerspective(from, to);
        },
        customCursor: 'move',
        offset: new Vector(
          -(this.width * (this.perspective.x / 2) - this.width / 2),
          -(this.height * this.perspective.y - this.height / 2)
        ),
        mobileScale: 3,
        render: ctx => {
          ctx.setShadow(0, 0, 2, 'rgba(0, 0, 0, 1)');
          const lineLength = 10;

          ctx
            .setStrokeStyle('white')
            .setLineWidth(2)
            .beginPath()
            .moveTo(-lineLength, 0)
            .lineTo(0, 0)
            .lineTo(lineLength, 0)
            .lineTo(0, 0)
            .lineTo(0, lineLength)
            .lineTo(0, 0)
            .lineTo(0, -lineLength)
            .stroke()
            .closePath();

          ctx
            .setFillStyle('white')
            .beginPath()
            .arc(0, 0, 4, 0, Math.PI * 2)
            .fill();
        },
      });
    }

    this.setControlLayer(perspectiveControlLayer);
  }

  _shiftPerspective(from, to) {
    if (this._shiftPerspectiveTimeout) {
      clearTimeout(this._shiftPerspectiveTimeout);
    }

    const delta = from.deltaTo(to).invert();

    this.perspective = this.perspective.translate(
      -delta.x / this.width,
      -delta.y / this.height
    );

    this.perspective.x = clamp(this.perspective.x, 0, 2);
    this.perspective.y = clamp(this.perspective.y, 0, 1);

    this.controls.perspective.offset =
      this.controls.perspective.offset.translate(delta.x, delta.y);

    this.controller?.render?.();

    // ! Perspective caching has been disabled for now
    // TODO: Determine if this can be permanently removed or restore it without rendering bugs

    //const timePerFrame = 1000 / this.options.perspective.maxFrameRate;
    // either set a timeout to update the frame in x milliseconds
    // or, if it has already been more than x milliseconds since we
    // last updated the frame, run it now
    // if (
    //   this.controller.params.exportMode ||
    //   !this.lastUpdatedPerspective ||
    //   Date.now() - this.lastUpdatedPerspective > timePerFrame
    // ) {
    //   this._updatePerspectiveCanvas();
    // } else {
    //   this._shiftPerspectiveTimeout = setTimeout(() => {
    //     this._updatePerspectiveCanvas();
    //   }, timePerFrame);
    // }

    this._updatePerspectiveCanvas();
  }

  _updatePerspectiveCanvas() {
    this.lastUpdatedPerspective = Date.now();

    if (this.perspectiveCanvas) {
      this.perspectiveCanvas.destroy();
      this.perspectiveCanvas = null;
    }

    this.controller?.render?.();
  }

  disablePerspectiveMode() {
    if (this.controlLayer === perspectiveControlLayer) {
      this.setControlLayer(defaultControlLayer);
    }
  }

  applyPerspective(mipMap) {
    if (
      this.controller.params.exportMode ||
      this.controller?.activeEl?.id === this.id
    ) {
      this.perspectiveCanvas?.destroy();
      this.perspectiveCanvas = null;
    }

    if (this.perspectiveCanvas) {
      return this.perspectiveCanvas;
    }

    const roundedWidth = Math.round(mipMap.width);
    const roundedHeight = Math.round(mipMap.height);

    const tmpPerspectiveCanvas = new SierraCanvas(roundedWidth, roundedHeight);

    const perspectiveX = this.perspective.x;
    const perspectiveY = this.perspective.y;

    const startHeight =
      perspectiveX < 1 ? mipMap.height : mipMap.height * (2 - perspectiveX);
    const endHeight =
      perspectiveX < 1 ? mipMap.height * perspectiveX : mipMap.height;

    const deltaPerStep = (startHeight - endHeight) / roundedWidth;

    // loop from left to right and draw one column of pixels at a time
    // the loop has to end at (width - 1) because a column with x coordinates of (width)
    // would be outside the image
    for (let i = 0; i < roundedWidth - 1; ++i) {
      tmpPerspectiveCanvas.drawImage(
        mipMap,
        i,
        0,
        1,
        mipMap.height,
        i,
        (mipMap.height - startHeight + deltaPerStep * i) * perspectiveY,
        1,
        startHeight - deltaPerStep * i
      );
    }

    const perspectiveRatio = perspectiveX < 1 ? perspectiveX : 2 - perspectiveX;
    const perspectiveOffset = roundedWidth - roundedWidth * perspectiveRatio;

    const perspectiveCanvas = new SierraCanvas(mipMap.width, mipMap.height);

    perspectiveCanvas.drawImage(
      tmpPerspectiveCanvas,
      perspectiveOffset / 2,
      0,
      roundedWidth * perspectiveRatio,
      roundedHeight
    );

    tmpPerspectiveCanvas.destroy();

    this.perspectiveCanvas = perspectiveCanvas;

    return perspectiveCanvas;
  }

  translate(x, y) {
    this.position = this.position.translate(x, y);
  }

  setControlLayer(layerName) {
    this.controller.eventBus?.emit('control-layer-changed', layerName);
    this.controlLayer = layerName;
  }

  requestRender() {
    this.controller?.render?.();
  }
}
